import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { smartUpload } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/Avatar";
import { fmtDateTime } from "@/components/badges";
import { FileText, Languages, Loader2, Paperclip, Send, Smile, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const REACTIONS = ["👍", "✅", "❤️", "🎉", "😄", "👀"];

function escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const CHAT_LANG_OPTS = [
  { v: "en", label: "English" },
  { v: "tr", label: "Türkçe" },
  { v: "ar", label: "العربية" },
  { v: "ru", label: "Русский" },
  { v: "zh", label: "中文" },
  { v: "hi", label: "हिन्दी" },
  { v: "ne", label: "नेपाली" },
];

export function ChatBox({ projectId, compact = false, room = "project" }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isCompany = room === "company";
  const roomKey = isCompany ? "company" : projectId;
  const base = isCompany ? "/company-chat" : `/projects/${projectId}/chat`;
  const [chatLang, setChatLang] = useState(() => localStorage.getItem("pruva-chat-lang") || user?.chat_language || user?.language || "en");
  const [showOrig, setShowOrig] = useState({});
  const changeLang = (v) => {
    setChatLang(v);
    localStorage.setItem("pruva-chat-lang", v);
    api.put("/profile/chat-language", { language: v }).catch(() => {});
  };
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [reactionFor, setReactionFor] = useState(null);
  const lastTypingRef = useRef(0);
  const listRef = useRef(null);
  const fileRef = useRef(null);
  const taRef = useRef(null);

  const { data } = useQuery({
    queryKey: ["chat", roomKey],
    queryFn: async () => (await api.get(`${base}/messages`)).data,
    enabled: isCompany || !!projectId,
    refetchInterval: 12000,
  });
  const messages = useMemo(() => data?.messages || [], [data]);

  const { data: membersData } = useQuery({
    queryKey: ["members", roomKey],
    queryFn: async () => (await api.get(isCompany ? "/company-chat/members" : `/projects/${projectId}/members`)).data,
    enabled: isCompany || !!projectId,
  });
  const members = useMemo(() => membersData?.members || [], [membersData]);

  const { data: typingData } = useQuery({
    queryKey: ["chat-typing", roomKey],
    queryFn: async () => (await api.get(`${base}/typing`)).data,
    enabled: isCompany || !!projectId,
    refetchInterval: 4000,
  });
  const typingNames = typingData?.typing || [];

  const lastId = messages.length ? messages[messages.length - 1].id : null;
  useEffect(() => {
    if (!roomKey || !lastId) return;
    api.post(`${base}/read`).then(() => {
      qc.invalidateQueries({ queryKey: ["chat-unread", roomKey] });
    });
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomKey, lastId, qc]);

  const sendMut = useMutation({
    mutationFn: async () =>
      (await api.post(`${base}/messages`, {
        text: text.trim(),
        mentions,
        attachment: isCompany ? null : attachment,
      })).data,
    onSuccess: () => {
      setText("");
      setMentions([]);
      setAttachment(null);
      qc.invalidateQueries({ queryKey: ["chat", roomKey] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const trMut = useMutation({
    mutationFn: async (id) => (await api.post(`/chat/messages/${id}/translate`, { language: chatLang })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat", roomKey] }),
    onError: (e) => toast.error(fmtErr(e)),
  });

  const requestedRef = useRef(new Set());
  const [autoPending, setAutoPending] = useState(false);
  useEffect(() => {
    if (user?.chat_language !== chatLang) {
      api.put("/profile/chat-language", { language: chatLang }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const ids = messages
      .filter((m) => m.text && !(m.translations || {})[chatLang] && !requestedRef.current.has(`${m.id}:${chatLang}`))
      .slice(-30)
      .map((m) => m.id);
    if (!ids.length) return;
    ids.forEach((id) => requestedRef.current.add(`${id}:${chatLang}`));
    setAutoPending(true);
    api
      .post("/chat/messages/translate-batch", { ids, language: chatLang })
      .then(() => qc.invalidateQueries({ queryKey: ["chat", roomKey] }))
      .catch(() => {})
      .finally(() => setAutoPending(false));
  }, [messages, chatLang, qc, roomKey]);

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/chat/messages/${id}`),
    onSuccess: () => {
      toast.success(t("chat.deleted"));
      qc.invalidateQueries({ queryKey: ["chat", roomKey] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const reactMut = useMutation({
    mutationFn: async ({ id, emoji }) => api.post(`/chat/messages/${id}/reactions`, { emoji }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat", roomKey] }),
    onError: (e) => toast.error(fmtErr(e)),
  });

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const res = await smartUpload(f, {}, null, `/projects/${projectId}/chat/uploads`);
      if (!res) throw new Error("Direct upload unavailable");
      setAttachment(res);
    } catch (err) {
      toast.error(fmtErr(err, t("chat.uploadFailed")));
    } finally {
      setUploading(false);
    }
  };

  const onTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    const now = Date.now();
    if (v.trim() && now - lastTypingRef.current > 3000) {
      lastTypingRef.current = now;
      api.post(`${base}/typing`).catch(() => {});
    }
    const upto = v.slice(0, e.target.selectionStart);
    const m = /(?:^|\s)@([^\s@]*)$/.exec(upto);
    if (m) {
      setMentionFilter(m[1].toLowerCase());
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const pickMention = (mem) => {
    const caret = taRef.current ? taRef.current.selectionStart : text.length;
    const upto = text.slice(0, caret).replace(/@[^\s@]*$/, `@${mem.name} `);
    setText(upto + text.slice(caret));
    setMentions((prev) => (prev.includes(mem.id) ? prev : [...prev, mem.id]));
    setMentionOpen(false);
    taRef.current?.focus();
  };

  const mentionCandidates = useMemo(() => {
    if (!mentionOpen) return [];
    return members
      .filter((m) => m.id !== user?.id && (m.name || "").toLowerCase().includes(mentionFilter))
      .slice(0, 6);
  }, [mentionOpen, members, mentionFilter, user?.id]);

  const namePattern = useMemo(() => {
    const names = members.map((m) => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
    if (!names.length) return null;
    return new RegExp(`(@(?:${names.map(escapeRe).join("|")}))`, "g");
  }, [members]);
  const mentionNames = useMemo(() => new Set(members.map((m) => `@${m.name}`)), [members]);

  const renderText = (t) => {
    if (!namePattern) return t;
    return t.split(namePattern).map((part, i) =>
      mentionNames.has(part) ? (
        <span key={`${i}-${part}`} className="rounded bg-amber-100 px-0.5 font-semibold text-amber-800">
          {part}
        </span>
      ) : (
        <span key={`${i}-${part}`}>{part}</span>
      )
    );
  };

  const downloadAttachment = (m) => {
    const a = document.createElement("a");
    a.href = `${process.env.REACT_APP_BACKEND_URL}/api/chat/messages/${m.id}/download`;
    a.download = m.attachment.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const canSend = !sendMut.isPending && (text.trim().length > 0 || !!attachment);

  return (
    <div className={`flex flex-col ${compact ? "h-[420px]" : "h-[calc(100vh-230px)]"}`} data-testid="chat-box">
      <div className="flex items-center justify-end gap-2 border-b bg-white px-3 py-1.5" data-testid="chat-lang-bar">
        <Languages className="h-3.5 w-3.5 text-slate-400" />
        <select value={chatLang} onChange={(e) => changeLang(e.target.value)} className="h-7 rounded border bg-white px-1.5 text-xs text-slate-600" data-testid="chat-lang-select">
          {CHAT_LANG_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
      </div>
      <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-3" data-testid="chat-message-list">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-center" data-testid="chat-empty">
            <p className="text-sm font-medium text-slate-600">{t("chat.emptyTitle")}</p>
            <p className="text-xs text-slate-400">{t("chat.emptyBody")}</p>
          </div>
        )}
        {messages.map((m) => {
          const own = m.user_id === user?.id;
          return (
            <div key={m.id} className={`group flex gap-2 ${own ? "flex-row-reverse" : ""}`} data-testid={`chat-message-${m.id}`}>
              <UserAvatar user={{ name: m.user_name, email: m.user_email }} />
              <div
                className={`relative max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                  own ? "bg-sky-600 text-white" : "border bg-white text-slate-800"
                }`}
              >
                <div className={`flex items-center gap-2 text-[11px] ${own ? "text-sky-100" : "text-slate-500"}`}>
                  <span className="font-semibold">{own ? t("chat.you") : m.user_name}</span>
                  <span className="font-mono">{fmtDateTime(m.created_at)}</span>
                  <button
                    onClick={() => setReactionFor(reactionFor === m.id ? null : m.id)}
                    className="opacity-0 transition-opacity group-hover:opacity-100"
                    title={t("chat.addReaction")}
                    data-testid={`chat-react-${m.id}`}
                  >
                    <Smile className="h-3 w-3" />
                  </button>
                  {(own || user?.is_super_admin || user?.is_admin) && (
                    <button
                      onClick={() => delMut.mutate(m.id)}
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                      title={t("chat.deleteMessage")}
                      data-testid={`chat-delete-${m.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {m.text && (() => {
                  const tr = (m.translations || {})[chatLang];
                  const translated = !!tr && tr !== m.text;
                  const pendingTr = !tr && autoPending && requestedRef.current.has(`${m.id}:${chatLang}`);
                  return (
                    <>
                      <p className="mt-0.5 whitespace-pre-wrap break-words">{renderText(translated && !showOrig[m.id] ? tr : m.text)}</p>
                      {translated ? (
                        <button
                          onClick={() => setShowOrig((s) => ({ ...s, [m.id]: !s[m.id] }))}
                          className={`mt-0.5 text-[10px] underline opacity-70 hover:opacity-100 ${own ? "text-sky-100" : "text-slate-400"}`}
                          data-testid={`chat-toggle-orig-${m.id}`}
                        >
                          {showOrig[m.id] ? t("chat.showTranslation") : t("chat.showOriginal")}
                        </button>
                      ) : tr ? null : pendingTr ? (
                        <span className={`mt-0.5 inline-flex items-center gap-1 text-[10px] opacity-60 ${own ? "text-sky-100" : "text-slate-400"}`} data-testid={`chat-translating-${m.id}`}>
                          <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        </span>
                      ) : (
                        <button
                          onClick={() => trMut.mutate(m.id)}
                          disabled={trMut.isPending}
                          className={`mt-0.5 text-[10px] underline opacity-60 hover:opacity-100 ${own ? "text-sky-100" : "text-slate-400"}`}
                          data-testid={`chat-translate-${m.id}`}
                        >
                          {t("chat.translate")}
                        </button>
                      )}
                    </>
                  );
                })()}
                {reactionFor === m.id && (
                  <div
                    className={`absolute top-full z-20 mt-1 flex gap-0.5 rounded-full border bg-white p-1 shadow-lg ${own ? "right-0" : "left-0"}`}
                    data-testid={`chat-reaction-picker-${m.id}`}
                  >
                    {REACTIONS.map((em, i) => (
                      <button
                        key={em}
                        className="rounded-full px-1.5 py-0.5 text-base transition-colors hover:bg-slate-100"
                        onClick={() => {
                          reactMut.mutate({ id: m.id, emoji: em });
                          setReactionFor(null);
                        }}
                        data-testid={`chat-react-option-${m.id}-${i}`}
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                )}
                {m.attachment && (
                  <button
                    onClick={() => downloadAttachment(m)}
                    className={`mt-1.5 flex items-center gap-1.5 rounded border px-2 py-1 text-xs transition-colors ${
                      own
                        ? "border-sky-400 text-sky-50 hover:bg-sky-500"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid={`chat-attachment-${m.id}`}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0" />
                    <span className="max-w-48 truncate">{m.attachment.filename}</span>
                    <span className="font-mono">{(m.attachment.size / 1024 / 1024).toFixed(2)} MB</span>
                  </button>
                )}
                {Object.keys(m.reactions || {}).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`chat-reactions-${m.id}`}>
                    {Object.entries(m.reactions).map(([em, users], i) => {
                      const mine = (users || []).includes(user?.id);
                      return (
                        <button
                          key={em}
                          onClick={() => reactMut.mutate({ id: m.id, emoji: em })}
                          className={`flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors ${
                            mine
                              ? "border-amber-400 bg-amber-100 text-amber-900"
                              : own
                                ? "border-sky-400 bg-sky-500/40 text-white"
                                : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                          }`}
                          data-testid={`chat-reaction-chip-${m.id}-${i}`}
                        >
                          <span>{em}</span>
                          <span className="font-mono">{(users || []).length}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {typingNames.length > 0 && (
        <div className="bg-slate-50 px-3 pb-2 text-xs italic text-slate-500" data-testid="chat-typing-indicator">
          {typingNames.length === 1 ? t("chat.typingOne", { name: typingNames[0] }) : t("chat.typingMany", { names: typingNames.join(", ") })}
        </div>
      )}

      <div className="border-t bg-white p-3">
        {attachment && (
          <div className="mb-2 flex items-center gap-2 rounded border bg-slate-50 px-2 py-1 text-xs text-slate-700" data-testid="chat-attachment-chip">
            <FileText className="h-3.5 w-3.5 shrink-0" />
            <span className="max-w-64 truncate">{attachment.filename}</span>
            <span className="font-mono text-slate-400">{(attachment.size / 1024 / 1024).toFixed(2)} MB</span>
            <button onClick={() => setAttachment(null)} className="ml-auto" data-testid="chat-attachment-remove">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <div className="relative flex items-end gap-2">
          {mentionOpen && mentionCandidates.length > 0 && (
            <div className="absolute bottom-full left-0 z-20 mb-1 w-72 overflow-hidden rounded-md border bg-white shadow-lg" data-testid="chat-mention-popover">
              {mentionCandidates.map((mem) => (
                <button
                  key={mem.id}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-100"
                  onClick={() => pickMention(mem)}
                  data-testid={`chat-mention-${mem.id}`}
                >
                  <UserAvatar user={mem} />
                  <span className="font-medium">{mem.name}</span>
                  <span className="truncate text-xs text-slate-400">{mem.email}</span>
                </button>
              ))}
            </div>
          )}
          <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} data-testid="chat-file-input" />
          {!isCompany && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              title={t("chat.attachFile")}
              data-testid="chat-attach-button"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </Button>
          )}
          <Textarea
            ref={taRef}
            value={text}
            onChange={onTextChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !mentionOpen) {
                e.preventDefault();
                if (canSend) sendMut.mutate();
              }
            }}
            rows={compact ? 1 : 2}
            placeholder={t("chat.placeholder")}
            className="min-h-10 flex-1 resize-none"
            data-testid="chat-input"
          />
          <Button
            onClick={() => sendMut.mutate()}
            disabled={!canSend}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="chat-send-button"
          >
            {sendMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
